import * as React from 'react';
import { useEffect, useState } from 'react';
import * as accounts from '@journeyapps-platform/types-accounts-hub';
import { CreateAppPayload, getSteps, TemplateOption } from './CreateAppPage';
import { FormikContext, useFormik } from 'formik';
import { FormLayoutSection, FormLayoutWidget } from '../../../widgets/layout/FormLayoutWidget';
import { InputWrapperWidget } from '../../../widgets/inputs';
import { CenterFormOrientation, CenterFormWidget } from '../../../widgets/layout/CenterFormWidget';
import { styled } from '../../../theme';
import { GroupedButtons } from '../../../widgets/buttons/GroupedButtons';
import { Keys, useKeyPress } from '../../../hooks/useKeyPress';
import { ParagraphWidget } from '../../../widgets/forms/ParagraphWidget';
import { CheckboxLabelWidget } from '../../../widgets/checkbox/CheckboxLabelWidget';
import { SelectWidget } from '../../../widgets/select/SelectWidget';
import { useSystem } from '../../../hooks/useSystem';
import { observer } from 'mobx-react';
import { ButtonType } from '../../../widgets/buttons/ButtonWidget';
import { CheckboxType } from '../../../widgets/checkbox/CheckboxWidget';
import { BreadCrumbWidget } from '../../../widgets/header/BreadCrumbWidget';
import { TemplateLanguage } from '@journeyapps-platform/types-alpha';
import { PRODUCT_CONFIG_MAP } from '@journeyapps-platform/types-accounts';
import { SmartProjectSelectWidget } from '../../../widgets-smart/SmartProjectSelectWidget';

export interface CreateAppStep3WidgetProps {
  payload: Partial<CreateAppPayload>;
  updatePayload: (payload: Partial<CreateAppPayload>) => Promise<void>;
  next: () => Promise<any>;
  back: () => any;
  onCancel: () => any;
}

interface Template {
  id: string;
  name: string;
  language?: string;
  default?: boolean;
}

const LanguageDisplay = {
  [TemplateLanguage.JavaScript]: 'JavaScript',
  [TemplateLanguage.TypeScript]: 'TypeScript',
  [TemplateLanguage.PowerSync]: 'PowerSync'
};

interface FormikValues {
  option: TemplateOption;
  language?: TemplateLanguage | null;
  template?: Template | null;
  cloneAppId?: string | null;
}

type CreateAppStep3Errors = {
  template?: string;
  cloneAppId?: string;
  language?: string;
};

export const CreateAppStep3Widget: React.FC<CreateAppStep3WidgetProps> = observer(
  (props: CreateAppStep3WidgetProps) => {
    const languages = PRODUCT_CONFIG_MAP[props.payload.type!].languages;

    const [templates, setTemplates] = useState<Template[]>([]);
    const [tempsLoading, setTempsLoading] = useState(true);
    const [selectedApp, setSelectedApp] = useState<accounts.SerializedAppResource>();
    const system = useSystem();

    useEffect(() => {
      (async () => {
        if (props.payload.org_id) {
          const client = system.clientProvisioner.provisionAlphaClient();
          const res = await client.getTemplates({});
          setTempsLoading(false);
          setTemplates(
            (res || []).filter((temp) => {
              // Filter out the TEST templates from alpha (only used for internal testing)
              return temp.name != 'TEST';
            })
          );
          formik.setValues({
            ...formik.values,
            option: TemplateOption.BLANK,
            template: getDefaultTemplate(TemplateLanguage.JavaScript),
            language: TemplateLanguage.JavaScript
          });
        }
      })();
    }, []);

    const formik = useFormik<FormikValues>({
      initialValues: {
        option: TemplateOption.BLANK
      },
      onSubmit: async (event) => {
        await props.next();
      },
      validateOnBlur: false,
      validate: (values) => {
        const errors: CreateAppStep3Errors = {};
        if (values.option == TemplateOption.BLANK && !values.language) {
          errors.language = 'Please select a language';
        }
        if (values.option == TemplateOption.TEMPLATE && !values.template) {
          errors.template = 'Please select a template';
        }
        if (values.option == TemplateOption.CLONE && !values.cloneAppId) {
          errors.cloneAppId = 'Please select a project';
        }
        return errors;
      }
    });

    useKeyPress(Keys.ENTER, {
      onKeyPressed: () => {
        formik.submitForm();
      }
    });

    const reportUpdate = (values: {
      option?: TemplateOption;
      template?: Template | null;
      language?: TemplateLanguage | null;
      original_app_id?: string | null;
    }) => {
      const newPayload: Partial<CreateAppPayload> = {
        ...props.payload,
        template_id: values.template?.id || 'standard'
      };

      if (values.original_app_id != undefined) {
        newPayload.original_app_id = values.original_app_id;
      }

      props.updatePayload(newPayload);
    };

    const getDefaultTemplate = (language: TemplateLanguage): Template | null => {
      // Filter on language and get default
      const template = templates.find((template) => template.language === language && template.default === true);
      return template || null;
    };

    const getTemplateOptions = (language?: TemplateLanguage | null): any[] => {
      // Filter on language and filter out default templates
      const filteredTemplates = templates.filter(
        (template) => template.language === language && template.default !== true
      );
      return filteredTemplates.map((temp) => {
        return { label: temp.name, value: temp.id };
      });
    };

    const getContent = (): FormLayoutSection => {
      return {
        key: 'template',
        elements: [
          <S.Container>
            <ParagraphWidget>{`Base your project on a template, clone an existing project or start with a blank canvas.`}</ParagraphWidget>
            <InputWrapperWidget>
              <S.Box>
                <S.Checkbox
                  label={<S.Label>{TemplateOption.BLANK}</S.Label>}
                  type={CheckboxType.CIRCLE}
                  checked={formik.values.option == TemplateOption.BLANK}
                  onChange={() => {
                    formik.setValues({
                      ...formik.values,
                      option: TemplateOption.BLANK,
                      cloneAppId: null,
                      template: getDefaultTemplate(TemplateLanguage.JavaScript),
                      language: TemplateLanguage.JavaScript
                    });
                    formik.setTouched({});
                    reportUpdate({ option: TemplateOption.BLANK, original_app_id: null });
                  }}
                />
                {formik.values.option == TemplateOption.BLANK && (
                  <S.SelectContainer>
                    <S.StyledSelect
                      isLoading={tempsLoading}
                      placeholder={'Select language'}
                      onChange={(value: any) => {
                        formik.setValues({
                          ...formik.values,
                          option: TemplateOption.BLANK,
                          template: getDefaultTemplate(value.value),
                          language: value.value
                        });
                        reportUpdate({
                          option: TemplateOption.BLANK,
                          language: value.value,
                          template: getDefaultTemplate(value.value)
                        });
                      }}
                      value={
                        formik.values.language
                          ? { label: LanguageDisplay[formik.values.language], value: formik.values.language }
                          : null
                      }
                      options={languages.map((language) => {
                        return { label: LanguageDisplay[language], value: language };
                      })}
                    />
                  </S.SelectContainer>
                )}
                {formik.touched.option && formik.errors.language && <S.Error>{formik.errors.language}</S.Error>}
              </S.Box>
              <S.Box>
                <S.Checkbox
                  label={<S.Label>{TemplateOption.TEMPLATE}</S.Label>}
                  checked={formik.values.option == TemplateOption.TEMPLATE}
                  type={CheckboxType.CIRCLE}
                  onChange={() => {
                    setSelectedApp(undefined);
                    formik.setValues({
                      ...formik.values,
                      option: TemplateOption.TEMPLATE,
                      cloneAppId: null,
                      language: formik.values.language || TemplateLanguage.JavaScript,
                      template: null
                    });
                    formik.setTouched({});
                    reportUpdate({ option: TemplateOption.TEMPLATE, original_app_id: null });
                  }}
                />
                {formik.values.option == TemplateOption.TEMPLATE && (
                  <S.Box>
                    <S.SelectContainer>
                      <S.SelectLabel>{`Language`}</S.SelectLabel>
                      <S.StyledSelect
                        isLoading={tempsLoading}
                        placeholder={'Select language'}
                        onChange={(value: any) => {
                          formik.setValues({
                            ...formik.values,
                            option: TemplateOption.TEMPLATE,
                            language: value.value,
                            template: null
                          });
                          reportUpdate({ option: TemplateOption.TEMPLATE, template: null });
                        }}
                        value={
                          formik.values.language
                            ? { label: LanguageDisplay[formik.values.language], value: formik.values.language }
                            : null
                        }
                        options={languages.map((language) => {
                          return { label: LanguageDisplay[language], value: language };
                        })}
                      />
                    </S.SelectContainer>
                    <S.SelectContainer>
                      <S.SelectLabel>{`Template`}</S.SelectLabel>
                      <S.StyledSelect
                        isLoading={tempsLoading}
                        placeholder={'Select template'}
                        onChange={(value: any) => {
                          const template = templates.find((value1) => {
                            return value1.id == value.value;
                          });
                          formik.setValues({ ...formik.values, option: TemplateOption.TEMPLATE, template: template });
                          reportUpdate({ option: TemplateOption.TEMPLATE, template: template });
                        }}
                        value={
                          formik.values.template
                            ? { label: formik.values.template.name, value: formik.values.template.id }
                            : null
                        }
                        options={getTemplateOptions(formik.values.language)}
                      />
                    </S.SelectContainer>
                  </S.Box>
                )}
                {formik.touched.option && formik.errors.template && <S.Error>{formik.errors.template}</S.Error>}
              </S.Box>
            </InputWrapperWidget>
            <InputWrapperWidget>
              <S.Box>
                <S.Checkbox
                  label={<S.Label>{TemplateOption.CLONE}</S.Label>}
                  type={CheckboxType.CIRCLE}
                  checked={formik.values.option == TemplateOption.CLONE}
                  onChange={() => {
                    formik.setValues({
                      ...formik.values,
                      option: TemplateOption.CLONE,
                      template: null,
                      language: null
                    });
                    formik.setTouched({});
                    reportUpdate({ option: TemplateOption.CLONE, template: null });
                  }}
                />
                {formik.values.option == TemplateOption.CLONE && (
                  <S.SelectContainer>
                    <SmartProjectSelectWidget
                      project={selectedApp}
                      onChange={(app) => {
                        setSelectedApp(app);
                        formik.setValues({ option: TemplateOption.CLONE, template: null, cloneAppId: app.id || null });
                        reportUpdate({
                          template: null,
                          original_app_id: app.id
                        });
                      }}
                    />
                  </S.SelectContainer>
                )}
                {formik.touched.option && formik.errors.cloneAppId && <S.Error>{formik.errors.cloneAppId}</S.Error>}
              </S.Box>
            </InputWrapperWidget>
          </S.Container>
        ]
      };
    };

    const getButtons = () => {
      return {
        key: 'submit',
        elements: [
          <GroupedButtons
            btns={[
              {
                label: 'Cancel',
                disabled: props.payload.loading,
                type: ButtonType.TERTIARY,
                action: () => {
                  props.onCancel();
                }
              },
              {
                label: 'Back',
                type: ButtonType.SECONDARY,
                disabled: props.payload.loading,
                action: () => {
                  props.back();
                }
              },
              {
                label: 'Create App',
                action: async (event, loading) => {
                  loading(true);
                  await formik.submitForm();
                  loading(false);
                }
              }
            ]}
          />
        ]
      };
    };

    return (
      <FormikContext.Provider value={formik}>
        <CenterFormWidget noLogo={true} primaryLabel={'Create new project'} orientation={CenterFormOrientation.ROW}>
          <BreadCrumbWidget steps={getSteps(props.payload.type!)} currentStep={2} />
          <FormLayoutWidget sections={[getContent(), getButtons()]} />
        </CenterFormWidget>
      </FormikContext.Provider>
    );
  }
);

namespace S {
  export const Container = styled.div`
    max-width: 386px;
  `;

  export const Box = styled.div`
    padding-bottom: 8px;
  `;

  export const Label = styled.div`
    font-size: 16px;
  `;

  export const Error = styled.div`
    color: ${(p) => p.theme.forms.input.error};
    font-size: 14px;
    user-select: text;
    padding-left: 26px;
  `;

  export const Checkbox = styled(CheckboxLabelWidget)``;

  export const StyledSelect = styled(SelectWidget)``;

  export const SelectContainer = styled.div`
    padding-top: 8px;
    padding-bottom: 8px;
    margin-left: 26px;
  `;

  export const SelectLabel = styled.div`
    color: ${(p) => p.theme.text.secondary};
    padding-bottom: 8px;
  `;
}
